import React, { useState, useEffect } from 'react';
import { 
  Network, 
  Settings, 
  Activity, 
  Route, 
  Wifi, 
  Server as ServerIcon,
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  Shield,
  Cable,
  Radio,
  Eye,
  EyeOff,
  Lock,
  FileDown
} from 'lucide-react';
import DashboardLayout from '../components/Nav';
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

const NetworkManagement = () => {
  const [interfaces, setInterfaces] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [stats, setStats] = useState([]);
  const [arpTable, setArpTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedInterface, setSelectedInterface] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // VPN state
  const [vpnServer, setVpnServer] = useState(null);
  const [vpnClients, setVpnClients] = useState([]);
  const [serverForm, setServerForm] = useState({
    interface: '',
    port: 1194,
    network_cidr: '10.250.0.0/24',
    dns: '',
    push_routes: '',
    name: 'server'
  });
  const [clientForm, setClientForm] = useState({
    client_name: '',
    static_ip: '',
    server_name: 'server'
  });
  const [vpnBusy, setVpnBusy] = useState(false);

  // Interface configuration state
  const [interfaceConfig, setInterfaceConfig] = useState({
    ip_address: '',
    prefix: 24,
    gateway: '',
    dns: ['8.8.8.8', '1.1.1.1']
  });

  // Route configuration state
  const [routeConfig, setRouteConfig] = useState({
    destination: '',
    gateway: '',
    interface: '',
    metric: 0
  });

  const [activeTab, setActiveTab] = useState('interfaces');
  useEffect(() => { document.title = "Dashboard | IP Intelligence Portal"; }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchNetworkData(), fetchVPNData()]);
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchNetworkData = async () => {
    try {
      const [interfacesRes, routesRes, statsRes, arpRes] = await Promise.all([
        fetch(`${API_BASE_URL}/network/interfaces`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/network/routes`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/network/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/network/arp`, { headers: getAuthHeaders() })
      ]);

      if (interfacesRes.ok) setInterfaces(await interfacesRes.json());
      if (routesRes.ok) setRoutes(await routesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (arpRes.ok) setArpTable(await arpRes.json());
    } catch (err) {
      console.error('Failed to fetch network data:', err);
    }
  };

  // ---- VPN API ----
  const fetchVPNData = async () => {
    try {
      const srv = await fetch(`${API_BASE_URL}/network/openvpn/server`, { headers: getAuthHeaders() });
  
      if (!srv.ok) {
        setVpnServer(null);
      } else {
        const data = await srv.json();
        // Treat {"exists":false,...} or any “thin” object as no server
        const looksReal =
          data &&
          data.exists !== false &&
          (typeof data.id === 'number' || typeof data.ID === 'number') &&
          !!data.listen_ip;
  
        setVpnServer(looksReal ? data : null);
      }
  
      const cl = await fetch(`${API_BASE_URL}/network/openvpn/clients`, { headers: getAuthHeaders() });
      setVpnClients(cl.ok ? await cl.json() : []);
    } catch (e) {
      console.error('Failed to fetch VPN data:', e);
      setVpnServer(null);
      setVpnClients([]);
    }
  };
  

  const createVPNServer = async () => {
    if (!serverForm.interface || !serverForm.network_cidr) {
      showMessage('Select an interface and VPN network CIDR', 'error');
      return;
    }
    setVpnBusy(true);
    try {
      const body = {
        interface: serverForm.interface,
        port: Number(serverForm.port) || 1194,
        network_cidr: serverForm.network_cidr,
        dns: parseCsv(serverForm.dns),
        push_routes: parseCsv(serverForm.push_routes),
        name: serverForm.name || 'server'
      };
      const res = await fetch(`${API_BASE_URL}/network/openvpn/server`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || 'Failed to create server');
      setVpnServer(data);
      showMessage('OpenVPN server created');
      await fetchVPNData();
    } catch (e) {
      showMessage(e.message || 'Failed to create VPN server', 'error');
    } finally {
      setVpnBusy(false);
    }
  };

  const deleteVPNServer = async () => {
    if (!vpnServer) return;
    if (!window.confirm(`Delete OpenVPN server "${vpnServer.name}"?\n\nThis will stop/disable the service, remove its config and client materials, and delete DB rows.`)) {
      return;
    }
    setVpnBusy(true);
    try {
      // prefer :name route; your backend also supports DELETE /openvpn/server without :name
      const res = await fetch(`${API_BASE_URL}/network/openvpn/server/${encodeURIComponent(vpnServer.name)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || 'Failed to delete server');

      showMessage(`Server "${vpnServer.name}" deleted`);
      setVpnServer(null);
      setVpnClients([]);
      await fetchVPNData();
    } catch (e) {
      showMessage(e.message || 'Failed to delete VPN server', 'error');
    } finally {
      setVpnBusy(false);
    }
  };

  const addVPNClient = async () => {
    if (!clientForm.client_name) {
      showMessage('Client name required', 'error');
      return;
    }
    setVpnBusy(true);
    try {
      const body = {
        client_name: clientForm.client_name.trim(),
        static_ip: clientForm.static_ip.trim() || undefined,
        server_name: clientForm.server_name || 'server'
      };
      const res = await fetch(`${API_BASE_URL}/network/openvpn/client`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || 'Failed to create client');

      showMessage(`Client ${data.client_name} created`);
      setClientForm({ client_name: '', static_ip: '', server_name: 'server' });
      await fetchVPNData();
      downloadClientRSC(data.id, data.client_name);
    } catch (e) {
      showMessage(e.message || 'Failed to create VPN client', 'error');
    } finally {
      setVpnBusy(false);
    }
  };

  const downloadClientRSC = async (id, clientName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/network/openvpn/client/${id}/rsc`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || 'Failed to fetch RSC');

      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ovpn-${clientName}.rsc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showMessage(e.message || 'Failed to download RSC', 'error');
    }
  };

  const deleteVPNClient = async (id) => {
    if (!window.confirm('Delete this client? This removes its stored certs/keys.')) return;
    setVpnBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/network/openvpn/client/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || 'Failed to delete client');
      showMessage('Client deleted');
      await fetchVPNData();
    } catch (e) {
      showMessage(e.message || 'Failed to delete client', 'error');
    } finally {
      setVpnBusy(false);
    }
  };

  // ---- helpers ----
  const safeJson = async (res) => { try { return await res.json(); } catch { return null; } };
  const parseCsv = (s) => (!s ? [] : s.split(',').map(v => v.trim()).filter(Boolean));

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message); setError(''); setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message); setSuccess(''); setTimeout(() => setError(''), 5000);
    }
  };

  const setInterfaceState = async (interfaceName, state) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/network/interfaces/${interfaceName}/state`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ state })
      });
      const data = await response.json();
      if (response.ok) { showMessage(data.message); fetchNetworkData(); }
      else { showMessage(data.error || 'Failed to change interface state', 'error'); }
    } catch {
      showMessage('Network error occurred', 'error');
    } finally { setLoading(false); }
  };

  const configureInterface = async () => {
    if (!selectedInterface || !interfaceConfig.ip_address) {
      showMessage('Please select an interface and provide an IP address', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/network/interfaces/${selectedInterface}/configure`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ interface: selectedInterface, ...interfaceConfig })
      });
      const data = await response.json();
      if (response.ok) {
        showMessage(data.message);
        fetchNetworkData();
        setInterfaceConfig({ ip_address: '', prefix: 24, gateway: '', dns: ['8.8.8.8', '1.1.1.1'] });
      } else {
        showMessage(data.error || 'Failed to configure interface', 'error');
      }
    } catch {
      showMessage('Network error occurred', 'error');
    } finally { setLoading(false); }
  };

  const addRoute = async () => {
    if (!routeConfig.destination) { showMessage('Please provide a destination', 'error'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/network/routes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(routeConfig)
      });
      const data = await response.json();
      if (response.ok) {
        showMessage(data.message);
        fetchNetworkData();
        setRouteConfig({ destination: '', gateway: '', interface: '', metric: 0 });
      } else {
        showMessage(data.error || 'Failed to add route', 'error');
      }
    } catch {
      showMessage('Network error occurred', 'error');
    } finally { setLoading(false); }
  };

  const deleteRoute = async (route) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/network/routes`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          destination: route.destination,
          gateway: route.gateway,
          interface: route.interface
        })
      });
      const data = await response.json();
      if (response.ok) { showMessage(data.message); fetchNetworkData(); }
      else { showMessage(data.error || 'Failed to delete route', 'error'); }
    } catch {
      showMessage('Network error occurred', 'error');
    } finally { setLoading(false); }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024; const sizes = ['B','KB','MB','GB','TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getInterfaceIcon = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'ethernet': case 'ether': return Cable;
      case 'wireless': case 'wifi': return Wifi;
      case 'loopback': return Radio;
      default: return Network;
    }
  };

  const getStateBadge = (state) =>
    state === 'UP'
      ? (<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>ONLINE</span>)
      : (<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>OFFLINE</span>);

  const MetricCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}><Icon className="w-6 h-6 text-white" /></div>
        <div>
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const totalInterfaces = interfaces.length;
  const onlineInterfaces = interfaces.filter(i => i.state === 'UP').length;
  const totalRoutes = routes.length;
  const totalArpEntries = arpTable.length;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="space-y-6 p-6">

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Network Management</h1>
              <p className="text-gray-600 mt-1">Manage network interfaces, routes, VPN, and monitor network activity</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showAdvanced ? 'Simple View' : 'Advanced View'}
              </button>
              <button
                onClick={fetchAll}
                disabled={loading || isRefreshing || vpnBusy}
                className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 text-sm ${loading || isRefreshing || vpnBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <span className="text-amber-800 text-sm font-medium">⚠️ Applying incorrect network configuration may cause daemon disconnection and loss of dashboard access.</span>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <MetricCard icon={Network} title="Network Interfaces" value={totalInterfaces} subtitle={`${onlineInterfaces} online`} color="bg-blue-500" />
            <MetricCard icon={Route} title="Active Routes" value={totalRoutes} subtitle="Routing entries" color="bg-purple-500" />
            <MetricCard icon={Shield} title="ARP Entries" value={totalArpEntries} subtitle="Network neighbors" color="bg-green-500" />
            <MetricCard icon={Lock} title="VPN Clients" value={vpnClients?.length || 0} subtitle={vpnServer ? `Server: ${vpnServer.name}` : 'Server not created'} color="bg-indigo-500" />
          </div>

          {/* Alerts */}
          {success && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-800 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="space-y-6">
            <div className="flex flex-wrap gap-4 overflow-x-auto">
              <Tab id="interfaces" active={activeTab} onClick={setActiveTab} icon={Network} count={totalInterfaces}>Interfaces</Tab>
              <Tab id="routes" active={activeTab} onClick={setActiveTab} icon={Route} count={totalRoutes}>Routes</Tab>
              <Tab id="stats" active={activeTab} onClick={setActiveTab} icon={Activity}>Statistics</Tab>
              <Tab id="arp" active={activeTab} onClick={setActiveTab} icon={ServerIcon} count={totalArpEntries}>ARP Table</Tab>
              <Tab id="vpn" active={activeTab} onClick={setActiveTab} icon={Lock} count={vpnClients?.length || 0}>VPN</Tab>
            </div>

            {/* Interfaces Tab */}
            {activeTab === 'interfaces' && (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* List */}
                <Card className="p-6">
                  <Header icon={Network} color="bg-blue-500" title="Network Interfaces" subtitle="Manage your network connections" />
                  <div className="space-y-4">
                    {interfaces.map((iface) => {
                      const IconComponent = getInterfaceIcon(iface.type);
                      return (
                        <div key={iface.name} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-gray-200">
                                <IconComponent className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <div className="flex items-center gap-3">
                                  <h4 className="font-semibold text-gray-900">{iface.name}</h4>
                                  {getStateBadge(iface.state)}
                                </div>
                                <p className="text-sm text-gray-600">{iface.type}</p>
                                {iface.mac_address && <p className="text-xs text-gray-500 font-mono">{iface.mac_address}</p>}
                              </div>
                            </div>
                            <button
                              className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors px-3 py-1.5 text-sm ${iface.state === 'UP' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                              onClick={() => setInterfaceState(iface.name, iface.state === 'UP' ? 'down' : 'up')}
                              disabled={loading}
                            >
                              {iface.state === 'UP' ? (<><ArrowDown className="w-4 h-4 mr-1" />Disable</>) : (<><ArrowUp className="w-4 h-4 mr-1" />Enable</>)}
                            </button>
                          </div>
                          {iface.ip_addresses?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">IP Addresses:</p>
                              <div className="flex flex-wrap gap-2">
                                {iface.ip_addresses.map((ip, idx) => (
                                  <span key={idx} className="px-2 py-1 text-xs font-mono bg-blue-100 text-blue-800 rounded border">{ip}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {showAdvanced && (
                            <div className="grid grid-cols-3 gap-4 text-xs text-gray-600 pt-3 border-t border-gray-200">
                              <div><span className="font-medium">MTU:</span> {iface.mtu}</div>
                              <div><span className="font-medium">Index:</span> {iface.index}</div>
                              <div><span className="font-medium">Flags:</span> {iface.flags?.join(', ')}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Configure */}
                <Card className="p-6">
                  <Header icon={Settings} color="bg-purple-500" title="Configure Interface" subtitle="Set IP addresses and network settings" />
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Interface</label>
                      <select
                        value={selectedInterface}
                        onChange={(e) => setSelectedInterface(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Choose an interface...</option>
                        {interfaces.map((iface) => (
                          <option key={iface.name} value={iface.name}>{iface.name} ({iface.type})</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <TextInput label="IP Address" placeholder="192.168.1.100" value={interfaceConfig.ip_address} onChange={(v) => setInterfaceConfig({ ...interfaceConfig, ip_address: v })} />
                      <NumberInput label="Prefix Length" min={1} max={32} value={interfaceConfig.prefix} onChange={(v) => setInterfaceConfig({ ...interfaceConfig, prefix: v })} />
                    </div>

                    <TextInput label="Gateway (Optional)" placeholder="192.168.1.1" value={interfaceConfig.gateway} onChange={(v) => setInterfaceConfig({ ...interfaceConfig, gateway: v })} />
                    <TextInput label="DNS Servers" placeholder="8.8.8.8,1.1.1.1" value={interfaceConfig.dns.join(',')} onChange={(v) => setInterfaceConfig({ ...interfaceConfig, dns: v.split(',').map(s => s.trim()) })} />

                    <button onClick={configureInterface} disabled={loading} className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 text-sm w-full">
                      <Settings className="w-4 h-4 mr-2" /> Configure Interface
                    </button>
                  </div>
                </Card>
              </div>
            )}

            {/* Routes Tab */}
            {activeTab === 'routes' && (
              <div className="grid gap-6 lg:grid-cols-2">
                <Card className="p-6">
                  <Header icon={Route} color="bg-green-500" title="Routing Table" subtitle="Network routing configuration" />
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {routes.map((route, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="p-2 rounded-lg bg-blue-100"><Globe className="w-4 h-4 text-blue-600" /></div>
                              <h4 className="font-semibold text-gray-900">{route.destination}</h4>
                            </div>
                            <div className="space-y-1 text-sm text-gray-600 ml-9">
                              {route.gateway && (<div><span className="font-medium">Gateway:</span> {route.gateway}</div>)}
                              <div><span className="font-medium">Interface:</span> {route.interface}</div>
                              {route.metric > 0 && (<div><span className="font-medium">Metric:</span> {route.metric}</div>)}
                            </div>
                          </div>
                          <button
                            className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-sm"
                            onClick={() => deleteRoute(route)}
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6">
                  <Header icon={Plus} color="bg-orange-500" title="Add Route" subtitle="Create new routing entry" />
                  <div className="space-y-4">
                    <TextInput label="Destination" placeholder="10.0.0.0/8 or default" value={routeConfig.destination} onChange={(v) => setRouteConfig({ ...routeConfig, destination: v })} />
                    <TextInput label="Gateway (Optional)" placeholder="192.168.1.1" value={routeConfig.gateway} onChange={(v) => setRouteConfig({ ...routeConfig, gateway: v })} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Interface (Optional)</label>
                      <select
                        value={routeConfig.interface}
                        onChange={(e) => setRouteConfig({ ...routeConfig, interface: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Choose an interface...</option>
                        {interfaces.map((iface) => (<option key={iface.name} value={iface.name}>{iface.name}</option>))}
                      </select>
                    </div>
                    <NumberInput label="Metric (Optional)" min={0} value={routeConfig.metric} onChange={(v) => setRouteConfig({ ...routeConfig, metric: v || 0 })} />
                    <button onClick={addRoute} disabled={loading} className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm w-full">
                      <Plus className="w-4 h-4 mr-2" /> Add Route
                    </button>
                  </div>
                </Card>
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'stats' && (
              <div className="space-y-6">
                {stats.map((stat) => (
                  <Card key={stat.interface} className="p-6">
                    <Header icon={Activity} color="bg-purple-500" title={`${stat.interface} Statistics`} subtitle="Network performance metrics" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                      <StatTile icon={ArrowDown} color="bg-blue-500" title="Received" value={formatBytes(stat.rx_bytes)} foot={`${stat.rx_packets.toLocaleString()} packets`} />
                      <StatTile icon={ArrowUp} color="bg-green-500" title="Transmitted" value={formatBytes(stat.tx_bytes)} foot={`${stat.tx_packets.toLocaleString()} packets`} />
                      <StatTile icon={Network} color="bg-purple-500" title="Total Traffic" value={formatBytes(stat.rx_bytes + stat.tx_bytes)} foot={`${(stat.rx_packets + stat.tx_packets).toLocaleString()} packets`} />
                      <StatTile icon={AlertCircle} color="bg-orange-500" title="Total Errors" value={stat.rx_errors + stat.tx_errors} foot={`${stat.rx_dropped + stat.tx_dropped} dropped`} />
                    </div>
                    {showAdvanced && (stat.rx_errors > 0 || stat.tx_errors > 0 || stat.rx_dropped > 0 || stat.tx_dropped > 0) && (
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Error Details</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <ErrorChip label="RX Errors" value={stat.rx_errors} />
                          <ErrorChip label="TX Errors" value={stat.tx_errors} />
                          <ErrorChip label="RX Dropped" value={stat.rx_dropped} yellow />
                          <ErrorChip label="TX Dropped" value={stat.tx_dropped} yellow />
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* ARP Table Tab */}
            {activeTab === 'arp' && (
              <Card className="p-6">
                <Header icon={ServerIcon} color="bg-indigo-500" title="ARP Table" subtitle="Network neighbor discovery and MAC address resolution" />
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-3 font-semibold text-gray-700">IP Address</th>
                        <th className="text-left p-3 font-semibold text-gray-700">MAC Address</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Interface</th>
                        <th className="text-left p-3 font-semibold text-gray-700">State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arpTable.map((entry, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 rounded-lg"><Globe className="w-4 h-4 text-blue-600" /></div>
                              <span className="font-mono text-sm font-medium">{entry.ip_address}</span>
                            </div>
                          </td>
                          <td className="p-3"><span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{entry.mac_address}</span></td>
                          <td className="p-3"><span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">{entry.interface}</span></td>
                          <td className="p-3"><span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">{entry.state}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* VPN Tab */}
            {activeTab === 'vpn' && (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Server Card */}
                <Card className="p-6">
                  <Header icon={Lock} color="bg-indigo-500" title="OpenVPN Server" subtitle="Create a server bound to a specific interface" />
                  {vpnServer ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="font-medium text-gray-700">Name:</span> {vpnServer.name}</div>
                        <div><span className="font-medium text-gray-700">Interface:</span> {vpnServer.interface}</div>
                        <div><span className="font-medium text-gray-700">Listen IP:</span> {vpnServer.listen_ip}</div>
                        <div><span className="font-medium text-gray-700">Port:</span> {vpnServer.port}</div>
                        <div className="col-span-2"><span className="font-medium text-gray-700">Network:</span> {vpnServer.network_cidr}</div>
                      </div>

                      {/* Danger Zone */}
                      <div className="mt-4 border-t pt-4">
                        <div className="text-sm text-gray-600 mb-2">Danger zone</div>
                        <button
                          onClick={deleteVPNServer}
                          disabled={vpnBusy}
                          className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors px-4 py-2 text-sm ${vpnBusy ? 'opacity-50 cursor-not-allowed' : ''} bg-red-500 hover:bg-red-600 text-white`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Server
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Select label="Bind Interface" value={serverForm.interface} onChange={(v) => setServerForm({ ...serverForm, interface: v })} options={interfaces.map(i => ({ value: i.name, label: `${i.name} (${i.type})` }))} />
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput label="Port" value={serverForm.port} onChange={(v) => setServerForm({ ...serverForm, port: v || 1194 })} />
                        <TextInput label="Name" value={serverForm.name} onChange={(v) => setServerForm({ ...serverForm, name: v })} />
                      </div>
                      <TextInput label="VPN Network (CIDR)" placeholder="10.250.0.0/24" value={serverForm.network_cidr} onChange={(v) => setServerForm({ ...serverForm, network_cidr: v })} />
                      <div className="grid grid-cols-2 gap-4">
                        <TextInput label="DNS (optional)" placeholder="8.8.8.8,1.1.1.1" value={serverForm.dns} onChange={(v) => setServerForm({ ...serverForm, dns: v })} />
                        <TextInput label="Push Routes (optional)" placeholder="10.0.0.0/8,172.16.0.0/12" value={serverForm.push_routes} onChange={(v) => setServerForm({ ...serverForm, push_routes: v })} />
                      </div>
                      <button
                        onClick={createVPNServer}
                        disabled={vpnBusy}
                        className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 text-sm ${vpnBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Lock className="w-4 h-4 mr-2" /> Create OpenVPN Server
                      </button>
                    </div>
                  )}
                </Card>

                {/* Clients Card */}
                <Card className="p-6">
                  <Header icon={ServerIcon} color="bg-green-600" title="OpenVPN Clients" subtitle="Add NAS clients and download MikroTik scripts" />
                  <div className="space-y-4">
                    {!vpnServer && (<div className="p-3 text-sm bg-yellow-50 border border-yellow-200 rounded">Create a server first, then add clients.</div>)}

                    <div className="grid grid-cols-3 gap-4">
                      <TextInput label="Client Name" placeholder="nas-01" value={clientForm.client_name} onChange={(v) => setClientForm({ ...clientForm, client_name: v })} />
                      <TextInput label="Static IP (optional)" placeholder="10.250.0.10" value={clientForm.static_ip} onChange={(v) => setClientForm({ ...clientForm, static_ip: v })} />
                      <TextInput label="Server Name" value={clientForm.server_name} onChange={(v) => setClientForm({ ...clientForm, server_name: v })} />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={addVPNClient}
                        disabled={vpnBusy || !vpnServer}
                        className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm ${vpnBusy || !vpnServer ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Client
                      </button>
                    </div>

                    <div className="border-t pt-4">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left p-3 font-semibold text-gray-700">Client</th>
                              <th className="text-left p-3 font-semibold text-gray-700">Static IP</th>
                              <th className="text-left p-3 font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(vpnClients || []).map((cl) => (
                              <tr key={cl.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="p-3 font-medium">{cl.client_name}</td>
                                <td className="p-3 font-mono text-sm">{cl.static_ip || '-'}</td>
                                <td className="p-3">
                                  <div className="flex gap-2">
                                    <button
                                      className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 text-sm"
                                      onClick={() => downloadClientRSC(cl.id, cl.client_name)}
                                    >
                                      <FileDown className="w-4 h-4 mr-1" /> RSC
                                    </button>
                                    <button
                                      className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-sm"
                                      onClick={() => deleteVPNClient(cl.id)}
                                      disabled={vpnBusy}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {(!vpnClients || vpnClients.length === 0) && (
                              <tr><td className="p-3 text-sm text-gray-500" colSpan="3">No clients yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// --- small UI helpers ---
const Card = ({ children, className = '' }) => <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>{children}</div>;
const Header = ({ icon: Icon, color, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className={`p-3 rounded-lg ${color}`}><Icon className="w-6 h-6 text-white" /></div>
    <div><h3 className="text-xl font-bold text-gray-900">{title}</h3><p className="text-gray-600">{subtitle}</p></div>
  </div>
);
const Tab = ({ id, active, onClick, icon: Icon, children, count }) => (
  <button
    onClick={() => onClick(id)}
    className={`flex items-center gap-3 px-6 py-3 rounded-lg font-medium transition-colors ${active === id ? 'bg-blue-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
  >
    <Icon className="w-5 h-5" />
    {children}
    {count !== undefined && (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${active === id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>{count}</span>
    )}
  </button>
);
const TextInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);
const NumberInput = ({ label, value, onChange, min, max }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);
const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">Choose...</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
const StatTile = ({ icon: Icon, color, title, value, foot }) => (
  <div className="text-center p-4 bg-gray-50 rounded-lg border">
    <div className={`${color} p-3 rounded-lg w-fit mx-auto mb-3`}><Icon className="w-5 h-5 text-white" /></div>
    <p className="text-2xl font-bold text-gray-800 mb-1">{value}</p>
    <p className="text-sm text-gray-600 font-medium">{title}</p>
    {foot && <p className="text-xs text-gray-500 mt-1">{foot}</p>}
  </div>
);
const ErrorChip = ({ label, value, yellow }) => (
  <div className={`text-center p-3 ${yellow ? 'bg-yellow-50' : 'bg-red-50'} rounded-lg`}>
    <p className={`text-xl font-bold ${yellow ? 'text-yellow-600' : 'text-red-600'}`}>{value}</p>
    <p className="text-xs text-gray-600">{label}</p>
  </div>
);

export default NetworkManagement;
